import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { PrzyciskDowodu } from '@/components/SzufladaDowodu';
import { DataQualityBadge } from '@/components/states';
import { config, describeDataOrigin, type DataOrigin } from '@/config/env';
import type { Sygnal } from './sygnaly';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * KARTA SYGNAŁU — anatomia jest kontraktem, nie układem
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * CO SIĘ STAŁO → DLACZEGO WAŻNE → DOWÓD → CO SPRAWDZIĆ.
 *
 * Kolejność jest tu ZAKODOWANA W DANYCH (tablica ANATOMIA), a nie wpisana ręcznie w JSX każdej
 * karty. Różnica jest praktyczna: przy ręcznym układzie wystarczy jedna karta napisana w innej
 * kolejności, żeby czytelnik musiał uczyć się każdego sygnału od nowa — a wtedy przestaje czytać
 * i zaczyna wierzyć. Tak zapisanego porządku nie da się przestawić dla pojedynczego przypadku.
 *
 * PRÓBA I JAKOŚĆ DANYCH SĄ W NAGŁÓWKU KARTY, nie w stopce ekranu. Karta bywa oglądana w oderwaniu
 * od reszty — na zrzucie, we wklejce, na rzutniku. „Spadek o 21%" bez informacji, że policzono to
 * na sześciu obserwacjach, produkuje złe decyzje szybciej niż brak ekranu.
 *
 * ŻADNEGO WSKAŹNIKA PEWNOŚCI. Nie ma tu „87% pewności" ani „siły sygnału 4/5", bo żadna taka
 * wielkość nie ma w tym produkcie definicji. Jakość danych ma definicję (lib/dataQuality.ts)
 * i dlatego wolno ją pokazać.
 */

/* Lista przeniesiona do `anatomia.ts`: ekran wyjaśnia tę samą anatomię, którą karta egzekwuje,
   a dwie kopie czterech kroków rozjechałyby się przy pierwszej zmianie nazwy. */
import { ANATOMIA, NAGLOWEK_KROKU, type Krok } from './anatomia';

/** Krótka etykieta pochodzenia danych na kartę; pełne zdanie idzie do tooltipa. */
const ETYKIETA_ZRODLA: Record<DataOrigin, string> = {
  'controlled-test-dataset': 'dane kontrolowane',
  'test-environment': 'środowisko TEST',
  production: 'dane produkcyjne',
  unconfigured: 'źródło nieustawione',
};

function Sekcja({ tytul, children }: { tytul: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
        {tytul}
      </Typography>
      {children}
    </Box>
  );
}

function WierszLiczby({ etykieta, wartosc }: { etykieta: string; wartosc: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed var(--linia)' }}>
      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
        {etykieta}
      </Typography>
      <Typography variant="caption" className="sygnal-liczba" sx={{ fontWeight: 600, textAlign: 'right' }}>
        {wartosc}
      </Typography>
    </Stack>
  );
}

export function KartaSygnalu({
  sygnal,
  kartaId,
}: {
  sygnal: Sygnal;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. „Wartość wg cennika" występuje
   * cztery razy na jednym ekranie, „Zamówienia w oknie" w sześciu plikach, a „przed czasem"
   * powtarza się raz na każdą szkołę w rankingu. Alternatywą było kotwiczenie po klasach MUI
   * i wartościach stylów — narzędzie, które działa dziś i zaczyna cicho kłamać po pierwszym
   * refaktorze wyglądu.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  function trescKroku(krok: Krok): ReactNode {
    switch (krok) {
      case 'CO_SIE_STALO':
        return <Typography variant="body2">{sygnal.coSieStalo}</Typography>;

      case 'DLACZEGO_WAZNE':
        return <Typography variant="body2">{sygnal.dlaczegoWazne}</Typography>;

      case 'DOWOD':
        return (
          <Box>
            <Box sx={{ mb: 1.5 }}>
              {sygnal.wierszeDowodu.map((w) => (
                <WierszLiczby key={w.etykieta} etykieta={w.etykieta} wartosc={w.wartosc} />
              ))}
            </Box>

            {/* Podstawa metryki PRZY liczbach: „wartość wg cennika" to nie przychód i karta
                musi to nieść ze sobą, także gdy ktoś ją zobaczy bez reszty ekranu. */}
            <MetricBasisChips basis={sygnal.podstawa} />

            <Stack direction="row" spacing={2} useFlexGap sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                Okno: {sygnal.okno}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                Porównanie: {sygnal.oknoPorownawcze}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                Silnik: {sygnal.silnik}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, ml: -1, flexWrap: 'wrap' }}>
              {sygnal.dowody.map((d) => (
                <PrzyciskDowodu key={d.etykieta} dowod={d.dowod} etykieta={d.etykieta} />
              ))}
            </Stack>
          </Box>
        );

      case 'CO_SPRAWDZIC':
        return (
          <Stack spacing={1}>
            {sygnal.coSprawdzic.map((k) => (
              <Box key={k.etykieta}>
                <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', fontWeight: 700 }}>
                  {k.etykieta}
                </Typography>
                <Typography variant="body2">{k.tresc}</Typography>
              </Box>
            ))}
          </Stack>
        );
    }
  }

  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <Chip size="small" label={sygnal.rodzaj} />
        <DataQualityBadge quality={sygnal.jakosc} reason={sygnal.jakoscPowod} />
        {/* SKĄD dane — osobno od tego, ILE są warte. To dwie różne rzeczy i obie jadą z liczbą. */}
        <Chip
          size="small"
          variant="outlined"
          color={config.dataOrigin === 'production' ? 'default' : 'warning'}
          label={ETYKIETA_ZRODLA[config.dataOrigin]}
          title={describeDataOrigin(config.dataOrigin)}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }} title={sygnal.probaOpis}>
          próba: {sygnal.proba}
        </Typography>
      </Stack>

      <Typography variant="h6" sx={{ mb: 2 }}>
        {sygnal.tytul}
      </Typography>

      {sygnal.ostrzezenia.map((o) => (
        <Box
          key={o}
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 'var(--r-maly)',
            background: 'var(--uwaga-tlo)',
            borderLeft: '4px solid var(--uwaga)',
          }}
        >
          <Typography variant="body2">{o}</Typography>
        </Box>
      ))}

      <Box sx={{ flexGrow: 1 }}>
        {ANATOMIA.map((krok) => (
          <Sekcja key={krok} tytul={NAGLOWEK_KROKU[krok]}>
            {trescKroku(krok)}
          </Sekcja>
        ))}
      </Box>

      {/*
        PRZYCZYNOWOŚĆ NIEUSTALONA — widoczna wprost, nie w tooltipie i nie w regulaminie.
        Zdanie „Związek przyczynowy nieustalony" zostaje dosłownie: stoi na nim publiczny test E2E,
        a poza tym jest jedynym miejscem, w którym produkt przyznaje granicę własnej wiedzy.
      */}
      <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid var(--linia)' }}>
        <Typography variant="overline" sx={{ color: 'var(--uwaga)', display: 'block' }}>
          Przyczynowość nieustalona
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--tekst-2)', display: 'block' }}>
          {sygnal.przyczynowosc}
        </Typography>
      </Box>
    </Paper>
  );
}
